import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, IconButton, Tooltip, Avatar } from "@mui/material";
import { Newspaper, Edit2, Trash2, Eye } from "lucide-react";
import { colors } from "../../../../theme";
import { CommonTable, CommonSwitch } from "../../ui";

/**
 * Helper to strip HTML tags for preview snippet
 */
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const formatDate = (dateVal) => {
  if (!dateVal) return "—";
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

/**
 * NewsTable Component
 * Renders news articles in a table layout with sorting and action controls.
 */
const NewsTable = ({
  newsList = [],
  loading = false,
  onEdit,
  onDelete,
  onToggleFeature,
  onPreview,
  page = 0,
  limit = 10,
}) => {
  const columns = [
    {
      id: "id",
      label: "#",
      width: "60px",
      align: "center",
      render: (_, __, index) => (
        <Typography
          variant="body2"
          sx={{ color: colors.slate[500], fontWeight: 600 }}
        >
          {page * limit + index + 1}
        </Typography>
      ),
    },
    {
      id: "title",
      label: "Article Details",
      minWidth: 320,
      render: (_, row) => {
        const snippet = stripHtml(row.description);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              variant="rounded"
              src={row.imageUrl}
              alt={row.title}
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                bgcolor: colors.slate[100],
                border: `1px solid ${colors.dashboard.cardBorder}`,
              }}
            >
              <Newspaper size={20} color={colors.slate[400]} />
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[900],
                  lineHeight: 1.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: 200, sm: 300, md: 420 },
                  cursor: onPreview ? "pointer" : "default",
                  "&:hover": onPreview
                    ? { color: colors.primary.main }
                    : undefined,
                }}
                onClick={() => onPreview && onPreview(row)}
              >
                {row.title || "Untitled Article"}
              </Typography>

              {snippet && (
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.slate[400],
                    display: "block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: { xs: 200, sm: 300, md: 420 },
                    mt: 0.25,
                  }}
                >
                  {snippet}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      id: "featured",
      label: "Featured Status",
      width: "160px",
      align: "center",
      render: (featured, row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <CommonSwitch
            checked={Boolean(featured)}
            onChange={() => onToggleFeature && onToggleFeature(row.id, featured)}
            activeLabel="Featured"
            inactiveLabel="Standard"
            size="small"
          />
        </Box>
      ),
    },
    {
      id: "updatedOn",
      label: "Published Date",
      width: "140px",
      render: (date, row) => (
        <Typography
          variant="body2"
          sx={{ color: colors.slate[600], fontSize: "0.85rem" }}
        >
          {formatDate(date || row.createdOn)}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      width: "130px",
      align: "right",
      render: (_, row) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.5,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Preview Article">
            <IconButton
              size="small"
              onClick={() => onPreview && onPreview(row)}
              sx={{
                color: colors.slate[500],
                "&:hover": {
                  color: colors.primary.main,
                  bgcolor: colors.primary.lighter,
                },
              }}
            >
              <Eye size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit Article">
            <IconButton
              size="small"
              onClick={() => onEdit && onEdit(row)}
              sx={{
                color: colors.slate[500],
                "&:hover": {
                  color: colors.primary.main,
                  bgcolor: colors.primary.lighter,
                },
              }}
            >
              <Edit2 size={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete Article">
            <IconButton
              size="small"
              onClick={() => onDelete && onDelete(row.id)}
              sx={{
                color: colors.slate[500],
                "&:hover": {
                  color: colors.error.main || "#ef4444",
                  bgcolor: colors.error.lighter || "#fee2e2",
                },
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${colors.dashboard.cardBorder}`,
        bgcolor: colors.background.paper,
      }}
    >
      <CommonTable
        columns={columns}
        rows={newsList}
        loading={loading}
        emptyMessage="No news articles found."
        rowKey="id"
      />
    </Box>
  );
};

NewsTable.propTypes = {
  newsList: PropTypes.array,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onToggleFeature: PropTypes.func,
  onPreview: PropTypes.func,
  page: PropTypes.number,
  limit: PropTypes.number,
};

export default NewsTable;
